import React, { useState, useEffect, useRef } from 'react';
import styled from 'styled-components';
import { ImageLoadingSpinner, SubtleImageLoadingSpinner } from './LoadingSpinner';

interface AnimatedGalleryImageProps {
    src: string;
    alt: string;
    onClick?: () => void;
    className?: string;
    style?: React.CSSProperties;
    onLoad?: () => void;
    onError?: () => void;
    preload?: boolean;
    showLoadingOverlay?: boolean;
    isThumbnail?: boolean;
    fetchPriority?: 'high' | 'low' | 'auto';
}

const ImageContainer = styled.div<{ $clickable?: boolean }>`
    position: relative;
    overflow: hidden;
    cursor: ${props => props.$clickable ? 'pointer' : 'default'};
    transition: opacity 0.3s ease-in-out;
`;

const StyledImage = styled.img<{ $loaded: boolean; $error: boolean; $isThumbnail?: boolean }>`
    width: 100%;
    height: 100%;
    opacity: ${props => props.$loaded && !props.$error ? 1 : 0};
    transition: opacity 0.3s ease-in-out;
    display: block;
    cursor: ${props => props.$isThumbnail ? 'pointer' : 'default'};

    ${props => props.$isThumbnail && `
        &:hover {
            opacity: 0.7;
        }
    `}
`;

const StyledVideo = styled.video<{ $loaded: boolean; $error: boolean; $isThumbnail?: boolean }>`
    width: 100%;
    height: auto;
    opacity: ${props => props.$loaded && !props.$error ? 1 : 0};
    transition: opacity 0.3s ease-in-out;
    display: block;
    cursor: ${props => props.$isThumbnail ? 'pointer' : 'default'};

    ${props => props.$isThumbnail && `
        &:hover {
            opacity: 0.7;
        }
    `}
`;

const ErrorPlaceholder = styled.div`
    width: 100%;
    height: 100%;
    background: #f8f9fa;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #6c757d;
    font-size: 14px;
    text-align: center;
    padding: 20px;
    border: 1px solid #dee2e6;
`;

export const AnimatedGalleryImage: React.FC<AnimatedGalleryImageProps> = ({
    src,
    alt,
    onClick,
    className,
    style,
    onLoad,
    onError,
    preload = false,
    showLoadingOverlay = true,
    isThumbnail = false,
    fetchPriority = 'auto',
}) => {
    const [loaded, setLoaded] = useState(false);
    const [error, setError] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [isWebM, setIsWebM] = useState(false);
    const imgRef = useRef<HTMLImageElement>(null);
    const videoRef = useRef<HTMLVideoElement>(null);

    // Check if the source is a WebM file
    useEffect(() => {
        if (src) {
            // Check if the URL ends with .webm or contains video/webm
            const isWebMFile = src.toLowerCase().includes('.webm') || 
                              src.toLowerCase().includes('video/webm') ||
                              src.toLowerCase().includes('webm');
            setIsWebM(isWebMFile);
        }
    }, [src]);

    useEffect(() => {
        if (!src) return;

        setLoaded(false);
        setError(false);
        setIsLoading(true);

        // Try to detect if this is a WebM file by attempting to load it as a video first
        const tryLoadAsVideo = () => {
            const video = document.createElement('video');
            
            video.onloadeddata = () => {
                setIsWebM(true);
                setLoaded(true);
                setIsLoading(false);
                onLoad?.();
            };

            video.onerror = () => {
                // If video loading fails, try as image
                tryLoadAsImage();
            };

            video.src = src;
            video.load();
        };

        const tryLoadAsImage = () => {
            const img = new Image();
            
            img.onload = () => {
                setIsWebM(false);
                setLoaded(true);
                setIsLoading(false);
                onLoad?.();
            };

            img.onerror = () => {
                setError(true);
                setIsLoading(false);
                onError?.();
            };

            img.src = src;
        };

        // If we already know it's WebM from URL detection, try video first
        if (isWebM) {
            tryLoadAsVideo();
        } else {
            // For unknown files, try video first, then fall back to image
            tryLoadAsVideo();
        }
    }, [src, isWebM, onLoad, onError]);

    const handleLoad = () => {
        setLoaded(true);
        setIsLoading(false);
        onLoad?.();
    };

    const handleError = () => {
        setError(true);
        setIsLoading(false);
        onError?.();
    };

    return (
        <ImageContainer 
            $clickable={!!onClick}
            onClick={onClick}
            className={className}
            style={style}
        >
            {error ? (
                <ErrorPlaceholder>
                    <div>
                        <div>⚠️</div>
                        <div>Failed to load {isWebM ? 'video' : 'image'}</div>
                    </div>
                </ErrorPlaceholder>
            ) : (
                <>
                    {isWebM ? (
                        <StyledVideo
                            ref={videoRef}
                            src={src}
                            $loaded={loaded}
                            $error={error}
                            $isThumbnail={isThumbnail}
                            onLoadedData={handleLoad}
                            onError={handleError}
                            loop
                            muted
                            autoPlay
                            playsInline
                            preload={preload ? 'auto' : 'metadata'}
                        />
                    ) : (
                        <StyledImage
                            ref={imgRef}
                            src={src}
                            alt={alt}
                            $loaded={loaded}
                            $error={error}
                            $isThumbnail={isThumbnail}
                            onLoad={handleLoad}
                            onError={handleError}
                            loading={preload ? 'eager' : 'lazy'}
                            fetchPriority={fetchPriority}
                        />
                    )}
                    {isLoading && showLoadingOverlay && (
                        isThumbnail ? (
                            <SubtleImageLoadingSpinner overlay={true} />
                        ) : (
                            <ImageLoadingSpinner overlay={true} />
                        )
                    )}
                </>
            )}
        </ImageContainer>
    );
};

// Specialized components for different use cases
export const AnimatedThumbnailImage: React.FC<Omit<AnimatedGalleryImageProps, 'showLoadingOverlay' | 'isThumbnail'>> = (props) => (
    <AnimatedGalleryImage {...props} showLoadingOverlay={true} isThumbnail={true} />
);

export const AnimatedFullSizeImage: React.FC<Omit<AnimatedGalleryImageProps, 'isThumbnail'>> = (props) => (
    <AnimatedGalleryImage {...props} showLoadingOverlay={true} isThumbnail={false} />
);

export const AnimatedPreloadableImage: React.FC<AnimatedGalleryImageProps> = (props) => (
    <AnimatedGalleryImage {...props} preload={true} />
);
